import React, { useRef } from 'react';
import { Printer, Download, X, Phone, Mail, MapPin, Car, User, Calendar, Route } from 'lucide-react';
import Button from '../ui/Button';
import { formatDateTime, formatCurrency, amountInWords } from '../../lib/utils';

const JourneyInvoice = ({ journey, travelsInfo, onClose }) => {
  const invoiceRef = useRef();

  const handlePrint = () => {
    // Generate clean HTML for printing without React components
    const invoiceNumber = `INV-${journey._id.slice(-8).toUpperCase()}`;
    const invoiceDate = journey.endTime ? new Date(journey.endTime) : new Date();

    // Cost breakdown calculation
    const getCostBreakdownForPrint = () => {
      const breakdown = [];
      const rentalType = journey.rentalType || journey.bookingId?.rentalType || 'per-km';
      const car = journey.carId;
      const actualDistance = journey.actualDistance || 0;
      const actualDuration = journey.actualDuration || 0;
      // Base fare: prefer the saved/edited baseCost so the printed invoice matches
      // the trip's recorded total. Only derive from rates when no baseCost is stored.
      const savedBase = journey.baseCost || 0;
      if (savedBase > 0) {
        let description = 'Base Fare';
        if (rentalType === 'km-based' || rentalType === 'per-km') description = 'Distance Charges';
        else if (rentalType === 'outstation') description = 'Outstation Charges';
        else if (rentalType === 'daily' || rentalType === 'rental') description = 'Daily Rental';
        else if (rentalType === 'local') description = 'Hourly Charges';
        breakdown.push({ description, details: actualDistance ? `${actualDistance} km` : 'Trip charges', amount: savedBase });
      } else if (rentalType === 'km-based' || rentalType === 'per-km') {
        const ratePerKm = car?.pricePerKm || 0;
        const calculatedBaseCost = actualDistance * ratePerKm;
        if (calculatedBaseCost > 0) {
          breakdown.push({ description: 'Distance Charges', details: `${actualDistance} km × ₹${ratePerKm}/km`, amount: calculatedBaseCost });
        }
      } else if (rentalType === 'daily' || rentalType === 'rental') {
        const days = Math.ceil(actualDuration / 24) || 1;
        const ratePerDay = car?.pricePerDay || 0;
        const calculatedBaseCost = days * ratePerDay;
        if (calculatedBaseCost > 0) {
          breakdown.push({ description: 'Daily Rental', details: `${days} day${days > 1 ? 's' : ''} × ₹${ratePerDay}/day`, amount: calculatedBaseCost });
        }
      } else if (rentalType === 'outstation') {
        const ratePerKm = car?.outstationPricePerKm || car?.pricePerKm || 0;
        const calculatedBaseCost = actualDistance * ratePerKm;
        if (calculatedBaseCost > 0) {
          breakdown.push({ description: 'Outstation Charges', details: `${actualDistance} km × ₹${ratePerKm}/km`, amount: calculatedBaseCost });
        }
        if (car?.driverBataPerDay) {
          const days = Math.ceil(actualDuration / 24) || 1;
          breakdown.push({ description: 'Driver Allowance', details: `${days} day${days > 1 ? 's' : ''} × ₹${car.driverBataPerDay}/day`, amount: days * car.driverBataPerDay });
        }
      } else if (rentalType === 'local') {
        const hours = Math.ceil(actualDuration) || journey.hours || 4;
        const ratePerHour = car?.pricePerHour || 0;
        const calculatedBaseCost = hours * ratePerHour;
        if (calculatedBaseCost > 0) {
          breakdown.push({ description: 'Hourly Charges', details: `${hours} hr${hours > 1 ? 's' : ''} × ₹${ratePerHour}/hr`, amount: calculatedBaseCost });
        }
      }

      if (journey.tollCharges > 0) {
        breakdown.push({ description: 'Toll Charges', details: 'Highway/Road toll', amount: journey.tollCharges });
      }
      if (journey.parkingCharges > 0) {
        breakdown.push({ description: 'Parking Charges', details: 'Parking fees', amount: journey.parkingCharges });
      }
      if (journey.otherCharges > 0) {
        breakdown.push({ description: 'Other Charges', details: journey.otherChargesNote || 'Additional charges', amount: journey.otherCharges });
      }

      return breakdown;
    };

    const costBreakdown = getCostBreakdownForPrint();
    const subtotal = costBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
    const discountAmount = journey.discount || 0;
    const totalCost = subtotal - discountAmount;

    // Real payment status from amount paid
    const paidAmount = journey.paidAmount || 0;
    const isPaid = totalCost > 0 && paidAmount >= totalCost;
    const statusLabel = isPaid ? 'PAID' : (paidAmount > 0 ? 'PARTIALLY PAID' : 'PENDING');
    const statusColor = isPaid ? '#059669' : '#d97706';
    const dueAmount = Math.max(0, totalCost - paidAmount);

    // Generate table rows
    const tableRows = costBreakdown.map((item, index) => `
      <tr style="background: ${index % 2 === 0 ? '#f8fafc' : '#ffffff'};">
        <td style="padding: 12px 15px; border-bottom: 1px solid #e5e7eb; font-size: 13px; font-weight: 500;">${item.description}</td>
        <td style="padding: 12px 15px; border-bottom: 1px solid #e5e7eb; font-size: 13px; color: #666;">${item.details}</td>
        <td style="padding: 12px 15px; border-bottom: 1px solid #e5e7eb; font-size: 13px; text-align: right; font-weight: 600;">₹${item.amount.toLocaleString('en-IN')}</td>
      </tr>
    `).join('');

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${invoiceNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              color: #1f2937;
              background: #fff;
              line-height: 1.5;
            }
            .invoice-wrapper {
              max-width: 800px;
              margin: 0 auto;
              padding: 30px;
            }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              .invoice-wrapper { padding: 15px; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-wrapper">
            <!-- Header -->
            <table style="width: 100%; margin-bottom: 25px; border-bottom: 3px solid #1e40af; padding-bottom: 20px;">
              <tr>
                <td style="vertical-align: top; width: 60%;">
                  ${travelsInfo?.logo?.url ? `<img src="${travelsInfo.logo.url}" alt="Logo" style="height: 50px; margin-bottom: 10px;">` : ''}
                  <h1 style="color: #1e40af; font-size: 24px; margin-bottom: 8px; font-weight: bold;">
                    ${travelsInfo?.name || 'Car Rental Services'}
                  </h1>
                  <div style="font-size: 12px; color: #666; line-height: 1.8;">
                    ${travelsInfo?.address ? `<p>📍 ${travelsInfo.address.street ? travelsInfo.address.street + ', ' : ''}${travelsInfo.address.city}, ${travelsInfo.address.state}</p>` : ''}
                    ${travelsInfo?.phone ? `<p>📞 ${travelsInfo.phone}</p>` : ''}
                    ${travelsInfo?.email ? `<p>✉️ ${travelsInfo.email}</p>` : ''}
                    ${travelsInfo?.gstNumber ? `<p style="font-weight: 600;">GST: ${travelsInfo.gstNumber}</p>` : ''}
                  </div>
                </td>
                <td style="vertical-align: top; text-align: right;">
                  <h2 style="color: #1e40af; font-size: 32px; font-weight: bold; margin-bottom: 10px;">INVOICE</h2>
                  <div style="font-size: 12px; color: #666; line-height: 2;">
                    <p><strong>Invoice #:</strong> ${invoiceNumber}</p>
                    <p><strong>Date:</strong> ${invoiceDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    <p><strong>Status:</strong> <span style="color: ${statusColor}; font-weight: 600;">${statusLabel}</span></p>
                    ${dueAmount > 0 ? `<p><strong>Amount Due:</strong> <span style="color: #d97706; font-weight: 600;">₹${dueAmount.toLocaleString('en-IN')}</span></p>` : ''}
                  </div>
                </td>
              </tr>
            </table>

            <!-- Customer & Journey Info -->
            <table style="width: 100%; margin-bottom: 25px;">
              <tr>
                <td style="width: 48%; vertical-align: top; background: #f8fafc; padding: 15px; border-radius: 8px;">
                  <h3 style="color: #1e40af; font-size: 11px; text-transform: uppercase; margin-bottom: 10px; font-weight: 600;">👤 Bill To</h3>
                  <p style="font-weight: 600; font-size: 14px; margin-bottom: 5px;">${journey.customerId?.name || 'N/A'}</p>
                  ${journey.customerId?.phone ? `<p style="font-size: 12px; color: #666;">${journey.customerId.phone}</p>` : ''}
                  ${journey.customerId?.email ? `<p style="font-size: 12px; color: #666;">${journey.customerId.email}</p>` : ''}
                </td>
                <td style="width: 4%;"></td>
                <td style="width: 48%; vertical-align: top; background: #f8fafc; padding: 15px; border-radius: 8px;">
                  <h3 style="color: #1e40af; font-size: 11px; text-transform: uppercase; margin-bottom: 10px; font-weight: 600;">🚗 Journey Details</h3>
                  <p style="font-size: 12px; margin-bottom: 3px;"><span style="color: #666;">From:</span> <strong>${journey.fromLocation?.address || 'N/A'}</strong></p>
                  ${(journey.stops && journey.stops.length) ? `<p style="font-size: 12px; margin-bottom: 3px;"><span style="color: #666;">Via:</span> <strong>${journey.stops.join(' → ')}</strong></p>` : ''}
                  <p style="font-size: 12px; margin-bottom: 3px;"><span style="color: #666;">To:</span> <strong>${journey.toLocation?.address || 'N/A'}</strong></p>
                  <p style="font-size: 12px;"><span style="color: #666;">Date:</span> <strong>${journey.startTime ? new Date(journey.startTime).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}</strong></p>
                </td>
              </tr>
            </table>

            <!-- Trip Stats Banner -->
            <table style="width: 100%; background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); border-radius: 8px; margin-bottom: 25px; color: white;">
              <tr>
                <td style="padding: 15px; text-align: center; border-right: 1px solid rgba(255,255,255,0.2);">
                  <p style="font-size: 10px; opacity: 0.8; margin-bottom: 3px;">VEHICLE</p>
                  <p style="font-weight: bold; font-size: 13px;">${journey.carId?.make || ''} ${journey.carId?.model || ''}</p>
                  <p style="font-size: 10px; opacity: 0.7;">${journey.carId?.registrationNumber || ''}</p>
                </td>
                <td style="padding: 15px; text-align: center; border-right: 1px solid rgba(255,255,255,0.2);">
                  <p style="font-size: 10px; opacity: 0.8; margin-bottom: 3px;">DISTANCE</p>
                  <p style="font-weight: bold; font-size: 20px;">${journey.actualDistance || 0}</p>
                  <p style="font-size: 10px; opacity: 0.7;">kilometers</p>
                </td>
                <td style="padding: 15px; text-align: center; border-right: 1px solid rgba(255,255,255,0.2);">
                  <p style="font-size: 10px; opacity: 0.8; margin-bottom: 3px;">DURATION</p>
                  <p style="font-weight: bold; font-size: 20px;">${journey.actualDuration?.toFixed(1) || 0}</p>
                  <p style="font-size: 10px; opacity: 0.7;">hours</p>
                </td>
                <td style="padding: 15px; text-align: center; border-right: 1px solid rgba(255,255,255,0.2);">
                  <p style="font-size: 10px; opacity: 0.8; margin-bottom: 3px;">START KM</p>
                  <p style="font-weight: bold; font-size: 20px;">${journey.startOdometer || 0}</p>
                </td>
                <td style="padding: 15px; text-align: center;">
                  <p style="font-size: 10px; opacity: 0.8; margin-bottom: 3px;">END KM</p>
                  <p style="font-weight: bold; font-size: 20px;">${journey.endOdometer || 0}</p>
                </td>
              </tr>
            </table>

            <!-- Driver Info -->
            ${journey.driverId ? `
            <table style="width: 100%; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 8px; margin-bottom: 25px;">
              <tr>
                <td style="padding: 12px 15px; width: 50px;">
                  <div style="width: 40px; height: 40px; background: #059669; border-radius: 50%; color: white; text-align: center; line-height: 40px; font-weight: bold;">
                    ${(journey.driverId?.name || 'D').charAt(0).toUpperCase()}
                  </div>
                </td>
                <td style="padding: 12px;">
                  <p style="font-size: 11px; color: #059669; font-weight: 600;">DRIVER</p>
                  <p style="font-weight: 600; font-size: 14px;">${journey.driverId?.name || 'Assigned Driver'}</p>
                </td>
                ${journey.driverId?.phone ? `
                <td style="padding: 12px; text-align: right;">
                  <p style="font-size: 11px; color: #666;">Contact</p>
                  <p style="font-weight: 600;">${journey.driverId.phone}</p>
                </td>
                ` : ''}
              </tr>
            </table>
            ` : ''}

            <!-- Cost Breakdown Table -->
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
              <thead>
                <tr style="background: #1e3a5f;">
                  <th style="padding: 12px 15px; text-align: left; color: white; font-size: 11px; text-transform: uppercase; font-weight: 600; border-radius: 6px 0 0 0;">Description</th>
                  <th style="padding: 12px 15px; text-align: left; color: white; font-size: 11px; text-transform: uppercase; font-weight: 600;">Details</th>
                  <th style="padding: 12px 15px; text-align: right; color: white; font-size: 11px; text-transform: uppercase; font-weight: 600; border-radius: 0 6px 0 0;">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>

            <!-- Totals -->
            <table style="width: 280px; margin-left: auto; margin-bottom: 25px;">
              <tr>
                <td style="padding: 8px 0; font-size: 13px; color: #666;">Subtotal</td>
                <td style="padding: 8px 0; font-size: 13px; text-align: right; font-weight: 500;">₹${subtotal.toLocaleString('en-IN')}</td>
              </tr>
              ${discountAmount > 0 ? `
              <tr>
                <td style="padding: 8px 0; font-size: 13px; color: #059669;">Discount ${journey.discountNote ? `(${journey.discountNote})` : ''}</td>
                <td style="padding: 8px 0; font-size: 13px; text-align: right; color: #059669; font-weight: 500;">- ₹${discountAmount.toLocaleString('en-IN')}</td>
              </tr>
              ` : ''}
              <tr>
                <td colspan="2" style="padding-top: 10px;">
                  <table style="width: 100%; background: #1e40af; border-radius: 6px;">
                    <tr>
                      <td style="padding: 12px 15px; color: white; font-weight: bold; font-size: 15px;">GRAND TOTAL</td>
                      <td style="padding: 12px 15px; color: white; font-weight: bold; font-size: 18px; text-align: right;">₹${totalCost.toLocaleString('en-IN')}</td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
            <p style="text-align: right; font-size: 11px; font-style: italic; color: #475569; margin: 0 0 20px;">${amountInWords(totalCost)}</p>

            <!-- Rate Info -->
            ${journey.carId && (journey.carId.pricePerKm > 0 || journey.carId.pricePerDay > 0) ? `
            <table style="width: 100%; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; margin-bottom: 25px;">
              <tr>
                <td style="padding: 12px 15px;">
                  <p style="font-size: 11px; font-weight: 600; color: #1e40af; text-transform: uppercase; margin-bottom: 8px;">Applicable Rates</p>
                  <p style="font-size: 12px;">
                    ${journey.carId.pricePerKm > 0 ? `<span style="margin-right: 20px;"><span style="color: #666;">Per KM:</span> <strong>₹${journey.carId.pricePerKm}</strong></span>` : ''}
                    ${journey.carId.pricePerDay > 0 ? `<span style="margin-right: 20px;"><span style="color: #666;">Per Day:</span> <strong>₹${journey.carId.pricePerDay}</strong></span>` : ''}
                    ${journey.carId.pricePerHour > 0 ? `<span><span style="color: #666;">Per Hour:</span> <strong>₹${journey.carId.pricePerHour}</strong></span>` : ''}
                  </p>
                </td>
              </tr>
            </table>
            ` : ''}

            <!-- Footer -->
            <table style="width: 100%; border-top: 2px solid #e5e7eb; padding-top: 20px;">
              <tr>
                <td style="width: 60%; vertical-align: top; padding-top: 15px;">
                  <h4 style="font-size: 11px; font-weight: 600; color: #1e40af; text-transform: uppercase; margin-bottom: 10px;">Terms & Conditions</h4>
                  <ul style="font-size: 11px; color: #666; line-height: 1.8; padding-left: 15px; margin: 0;">
                    <li>This is a computer-generated invoice.</li>
                    <li>Payment received with thanks.</li>
                    <li>Toll and parking charges are as per actuals.</li>
                    <li>For any queries, contact us within 7 days.</li>
                  </ul>
                </td>
                <td style="width: 40%; vertical-align: top; padding-top: 15px; text-align: right;">
                  <h4 style="font-size: 11px; font-weight: 600; color: #1e40af; text-transform: uppercase; margin-bottom: 10px;">Payment Method</h4>
                  <p style="font-size: 12px; color: #666;">Cash / UPI / Bank Transfer</p>
                </td>
              </tr>
            </table>

            <!-- Thank You -->
            <table style="width: 100%; margin-top: 25px; background: linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%); border-radius: 8px;">
              <tr>
                <td style="padding: 20px; text-align: center;">
                  <p style="color: #1e40af; font-weight: 600; font-size: 14px;">Thank you for choosing ${travelsInfo?.name || 'our services'}!</p>
                  <p style="color: #666; font-size: 12px; margin-top: 5px;">We look forward to serving you again.</p>
                </td>
              </tr>
            </table>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();

    // Wait for images to load before printing
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Calculate cost breakdown
  const getCostBreakdown = () => {
    const breakdown = [];
    const rentalType = journey.rentalType || journey.bookingId?.rentalType || 'per-km';
    const car = journey.carId;
    const actualDistance = journey.actualDistance || 0;
    const actualDuration = journey.actualDuration || 0;

    // Base fare: prefer the saved/edited baseCost so the invoice matches the
    // trip's recorded total. Only derive from rates when no baseCost is stored.
    const savedBase = journey.baseCost || 0;
    if (savedBase > 0) {
      let description = 'Base Fare';
      if (rentalType === 'km-based' || rentalType === 'per-km') description = 'Distance Charges';
      else if (rentalType === 'outstation') description = 'Outstation Charges';
      else if (rentalType === 'daily' || rentalType === 'rental') description = 'Daily Rental';
      else if (rentalType === 'local') description = 'Hourly Charges';
      breakdown.push({ description, details: actualDistance ? `${actualDistance} km` : 'Trip charges', amount: savedBase });
    } else if (rentalType === 'km-based' || rentalType === 'per-km') {
      const ratePerKm = car?.pricePerKm || 0;
      const calculatedBaseCost = actualDistance * ratePerKm;
      if (calculatedBaseCost > 0) {
        breakdown.push({ description: `Distance Charges`, details: `${actualDistance} km × ${formatCurrency(ratePerKm)}/km`, amount: calculatedBaseCost });
      }
    } else if (rentalType === 'daily' || rentalType === 'rental') {
      const days = Math.ceil(actualDuration / 24) || 1;
      const ratePerDay = car?.pricePerDay || 0;
      const calculatedBaseCost = days * ratePerDay;
      if (calculatedBaseCost > 0) {
        breakdown.push({ description: `Daily Rental`, details: `${days} day${days > 1 ? 's' : ''} × ${formatCurrency(ratePerDay)}/day`, amount: calculatedBaseCost });
      }
    } else if (rentalType === 'outstation') {
      const ratePerKm = car?.outstationPricePerKm || car?.pricePerKm || 0;
      const calculatedBaseCost = actualDistance * ratePerKm;
      if (calculatedBaseCost > 0) {
        breakdown.push({ description: `Outstation Charges`, details: `${actualDistance} km × ${formatCurrency(ratePerKm)}/km`, amount: calculatedBaseCost });
      }
      if (car?.driverBataPerDay) {
        const days = Math.ceil(actualDuration / 24) || 1;
        breakdown.push({ description: `Driver Allowance`, details: `${days} day${days > 1 ? 's' : ''} × ${formatCurrency(car.driverBataPerDay)}/day`, amount: days * car.driverBataPerDay });
      }
    } else if (rentalType === 'local') {
      const hours = Math.ceil(actualDuration) || journey.hours || 4;
      const ratePerHour = car?.pricePerHour || 0;
      const calculatedBaseCost = hours * ratePerHour;
      if (calculatedBaseCost > 0) {
        breakdown.push({ description: `Hourly Charges`, details: `${hours} hr${hours > 1 ? 's' : ''} × ${formatCurrency(ratePerHour)}/hr`, amount: calculatedBaseCost });
      }
    }

    if (journey.tollCharges > 0) {
      breakdown.push({
        description: 'Toll Charges',
        details: 'Highway/Road toll',
        amount: journey.tollCharges
      });
    }

    if (journey.parkingCharges > 0) {
      breakdown.push({
        description: 'Parking Charges',
        details: 'Parking fees',
        amount: journey.parkingCharges
      });
    }

    if (journey.otherCharges > 0) {
      breakdown.push({
        description: 'Other Charges',
        details: journey.otherChargesNote || 'Additional charges',
        amount: journey.otherCharges
      });
    }

    return breakdown;
  };

  const costBreakdown = getCostBreakdown();
  const subtotal = costBreakdown.reduce((sum, item) => sum + (item.amount || 0), 0);
  const discountAmount = journey.discount || 0;
  const totalCost = subtotal - discountAmount;

  // Real payment status from amount paid
  const paidAmount = journey.paidAmount || 0;
  const isPaid = totalCost > 0 && paidAmount >= totalCost;
  const statusLabel = isPaid ? 'PAID' : (paidAmount > 0 ? 'PARTIALLY PAID' : 'PENDING');
  const dueAmount = Math.max(0, totalCost - paidAmount);

  const invoiceNumber = `INV-${journey._id.slice(-8).toUpperCase()}`;
  const invoiceDate = journey.endTime ? new Date(journey.endTime) : new Date();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header Actions */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <h2 className="text-lg font-bold text-gray-800">Invoice Preview</h2>
          <div className="flex gap-2">
            <Button size="sm" onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700">
              <Printer className="h-4 w-4 mr-1" />
              Print Invoice
            </Button>
            <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Invoice Content */}
        <div className="overflow-y-auto flex-1 p-6 bg-gray-100">
          <div ref={invoiceRef} className="invoice-container bg-white shadow-lg rounded-lg p-8 max-w-3xl mx-auto">

            {/* Header */}
            <div className="flex justify-between items-start mb-8 pb-6 border-b-4 border-blue-800">
              <div className="company-info">
                {travelsInfo?.logo?.url ? (
                  <img src={travelsInfo.logo.url} alt="Logo" className="h-16 mb-3" />
                ) : null}
                <h1 className="text-2xl font-bold text-blue-800">
                  {travelsInfo?.name || 'Car Rental Services'}
                </h1>
                <div className="text-sm text-gray-600 mt-2 space-y-1">
                  {travelsInfo?.address && (
                    <p className="flex items-center">
                      <MapPin className="h-3 w-3 mr-1" />
                      {travelsInfo.address.street && `${travelsInfo.address.street}, `}
                      {travelsInfo.address.city}, {travelsInfo.address.state}
                    </p>
                  )}
                  {travelsInfo?.phone && (
                    <p className="flex items-center">
                      <Phone className="h-3 w-3 mr-1" />
                      {travelsInfo.phone}
                    </p>
                  )}
                  {travelsInfo?.email && (
                    <p className="flex items-center">
                      <Mail className="h-3 w-3 mr-1" />
                      {travelsInfo.email}
                    </p>
                  )}
                  {travelsInfo?.gstNumber && (
                    <p className="font-medium">GST: {travelsInfo.gstNumber}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <h2 className="text-4xl font-bold text-blue-800">INVOICE</h2>
                <div className="mt-3 text-sm text-gray-600">
                  <p><span className="font-medium">Invoice #:</span> {invoiceNumber}</p>
                  <p><span className="font-medium">Date:</span> {invoiceDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                  <p><span className="font-medium">Status:</span> <span className={`font-medium ${isPaid ? 'text-green-600' : 'text-amber-600'}`}>{statusLabel}</span></p>
                  {dueAmount > 0 && (
                    <p><span className="font-medium">Amount Due:</span> <span className="text-amber-600 font-medium">{formatCurrency(dueAmount)}</span></p>
                  )}
                </div>
              </div>
            </div>

            {/* Customer & Journey Meta */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              {/* Bill To */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-blue-800 uppercase mb-3 flex items-center">
                  <User className="h-3 w-3 mr-1" />
                  Bill To
                </h3>
                <p className="font-semibold text-gray-800">{journey.customerId?.name || 'N/A'}</p>
                {journey.customerId?.phone && (
                  <p className="text-sm text-gray-600">{journey.customerId.phone}</p>
                )}
                {journey.customerId?.email && (
                  <p className="text-sm text-gray-600">{journey.customerId.email}</p>
                )}
              </div>

              {/* Journey Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-xs font-semibold text-blue-800 uppercase mb-3 flex items-center">
                  <Route className="h-3 w-3 mr-1" />
                  Journey Details
                </h3>
                <div className="text-sm space-y-1">
                  <p><span className="text-gray-500">From:</span> <span className="font-medium">{journey.fromLocation?.address || 'N/A'}</span></p>
                  {journey.stops && journey.stops.length > 0 && (
                    <p><span className="text-gray-500">Via:</span> <span className="font-medium">{journey.stops.join(' → ')}</span></p>
                  )}
                  <p><span className="text-gray-500">To:</span> <span className="font-medium">{journey.toLocation?.address || 'N/A'}</span></p>
                  <p><span className="text-gray-500">Date:</span> <span className="font-medium">{formatDateTime(journey.startTime)}</span></p>
                </div>
              </div>
            </div>

            {/* Vehicle & Trip Stats */}
            <div className="bg-gradient-to-r from-blue-800 to-blue-600 text-white rounded-lg p-5 mb-8">
              <div className="grid grid-cols-5 gap-4 text-center">
                <div className="bg-white/10 rounded-lg p-3">
                  <Car className="h-5 w-5 mx-auto mb-1 opacity-80" />
                  <p className="text-xs opacity-80">Vehicle</p>
                  <p className="font-bold text-sm">{journey.carId?.make} {journey.carId?.model}</p>
                  <p className="text-xs opacity-70">{journey.carId?.registrationNumber}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <Route className="h-5 w-5 mx-auto mb-1 opacity-80" />
                  <p className="text-xs opacity-80">Distance</p>
                  <p className="font-bold text-lg">{journey.actualDistance || 0}</p>
                  <p className="text-xs opacity-70">kilometers</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <Calendar className="h-5 w-5 mx-auto mb-1 opacity-80" />
                  <p className="text-xs opacity-80">Duration</p>
                  <p className="font-bold text-lg">{journey.actualDuration?.toFixed(1) || 0}</p>
                  <p className="text-xs opacity-70">hours</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-xs opacity-80 mt-1">Start Reading</p>
                  <p className="font-bold text-lg">{journey.startOdometer || 0}</p>
                  <p className="text-xs opacity-70">km</p>
                </div>
                <div className="bg-white/10 rounded-lg p-3">
                  <p className="text-xs opacity-80 mt-1">End Reading</p>
                  <p className="font-bold text-lg">{journey.endOdometer || 0}</p>
                  <p className="text-xs opacity-70">km</p>
                </div>
              </div>
            </div>

            {/* Driver Info */}
            {journey.driverId && (
              <div className="flex items-center gap-4 mb-6 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-green-700 font-medium">Driver</p>
                  <p className="font-semibold text-gray-800">{journey.driverId?.name || 'Assigned Driver'}</p>
                </div>
                {journey.driverId?.phone && (
                  <div className="ml-auto text-right">
                    <p className="text-xs text-gray-500">Contact</p>
                    <p className="font-medium text-gray-700">{journey.driverId.phone}</p>
                  </div>
                )}
              </div>
            )}

            {/* Cost Breakdown Table */}
            <table className="w-full mb-6">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="text-left p-3 text-xs uppercase font-semibold rounded-tl-lg">Description</th>
                  <th className="text-left p-3 text-xs uppercase font-semibold">Details</th>
                  <th className="text-right p-3 text-xs uppercase font-semibold rounded-tr-lg">Amount</th>
                </tr>
              </thead>
              <tbody>
                {costBreakdown.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-3 text-sm font-medium text-gray-800">{item.description}</td>
                    <td className="p-3 text-sm text-gray-600">{item.details}</td>
                    <td className="p-3 text-sm text-right font-semibold">{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-72">
                <div className="flex justify-between py-2 text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(subtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between py-2 text-sm text-green-600">
                    <span>Discount {journey.discountNote && <span className="text-xs">({journey.discountNote})</span>}</span>
                    <span className="font-medium">- {formatCurrency(discountAmount)}</span>
                  </div>
                )}
                <div className="py-4 mt-2 bg-blue-800 text-white rounded-lg px-4">
                  <div className="flex justify-between">
                    <span className="font-bold text-lg">GRAND TOTAL</span>
                    <span className="font-bold text-xl">{formatCurrency(totalCost)}</span>
                  </div>
                  <p className="text-white/80 text-[11px] italic mt-1 text-right">{amountInWords(totalCost)}</p>
                </div>
              </div>
            </div>

            {/* Rate Info */}
            {journey.carId && (journey.carId.pricePerKm > 0 || journey.carId.pricePerDay > 0) && (
              <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="text-xs font-semibold text-blue-800 uppercase mb-2">Applicable Rates</h4>
                <div className="flex gap-6 text-sm">
                  {journey.carId.pricePerKm > 0 && (
                    <span><span className="text-gray-500">Per KM:</span> <span className="font-semibold">{formatCurrency(journey.carId.pricePerKm)}</span></span>
                  )}
                  {journey.carId.pricePerDay > 0 && (
                    <span><span className="text-gray-500">Per Day:</span> <span className="font-semibold">{formatCurrency(journey.carId.pricePerDay)}</span></span>
                  )}
                  {journey.carId.pricePerHour > 0 && (
                    <span><span className="text-gray-500">Per Hour:</span> <span className="font-semibold">{formatCurrency(journey.carId.pricePerHour)}</span></span>
                  )}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="border-t-2 border-gray-200 pt-6">
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <h4 className="text-xs font-semibold text-blue-800 uppercase mb-3">Terms & Conditions</h4>
                  <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
                    <li>This is a computer-generated invoice.</li>
                    <li>Payment received with thanks.</li>
                    <li>Toll and parking charges are as per actuals.</li>
                    <li>For any queries, contact us within 7 days.</li>
                  </ul>
                </div>
                <div className="text-right">
                  <h4 className="text-xs font-semibold text-blue-800 uppercase mb-3">Payment Method</h4>
                  <p className="text-sm text-gray-600">Cash / UPI / Bank Transfer</p>
                </div>
              </div>
            </div>

            {/* Thank You */}
            <div className="mt-8 text-center py-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg">
              <p className="text-blue-800 font-semibold">Thank you for choosing {travelsInfo?.name || 'our services'}!</p>
              <p className="text-sm text-gray-500 mt-1">We look forward to serving you again.</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyInvoice;
